import { useEffect, useRef, useState } from 'react';
import KioskLock from './KioskLock';

const AUTO_LOCK_MS = 5 * 60 * 1000;

const KioskShell = ({
  operator,
  tabs = [],
  activeTab,
  onTabChange,
  children,
}) => {
  const [locked, setLocked] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const lockTimerRef = useRef(null);

  useEffect(() => {
    if (!unlocked) return undefined;
    const resetTimer = () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      lockTimerRef.current = setTimeout(() => setLocked(true), AUTO_LOCK_MS);
    };

    const events = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true }),
    );
    resetTimer();

    return () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [unlocked]);

  useEffect(() => {
    const handleLockRequest = () => setLocked(true);
    window.addEventListener('brissbella:kiosk:lock', handleLockRequest);
    return () =>
      window.removeEventListener('brissbella:kiosk:lock', handleLockRequest);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('brissbella:kiosk:active', { detail: { active: true } }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent('brissbella:kiosk:active', { detail: { active: false } }),
      );
    };
  }, []);

  if (!unlocked || locked) {
    return (
      <KioskLock
        operator={operator}
        onUnlock={() => {
          setUnlocked(true);
          setLocked(false);
        }}
      />
    );
  }

  return (
    <div className="-mx-3 -mt-4 -mb-24 flex min-h-[calc(100vh-4rem)] flex-col bg-stone-50 sm:-mx-6 sm:-mt-5 md:-mb-5 lg:-mx-8 lg:-mt-6 lg:-mb-6">
      <main className="flex-1 px-3 pb-28 pt-4 sm:px-6 lg:px-8">{children}</main>

      {tabs.length ? (
        <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-stone-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur">
          <div className="grid grid-cols-5">
            {tabs.map((tab) => {
              const active = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange?.(tab.id)}
                  className={`flex min-h-[64px] flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition ${
                    active ? 'text-[#d61f74]' : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  <span className="text-xl leading-none">{tab.icon}</span>
                  <span className="truncate">{tab.label}</span>
                  {tab.badge ? (
                    <span
                      className={`mt-0.5 rounded-full px-1.5 text-[10px] font-semibold ${
                        active ? 'bg-[#fce7f3] text-[#b91c66]' : 'bg-stone-100 text-stone-600'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </nav>
      ) : null}
    </div>
  );
};

export default KioskShell;
